import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, BUTTON_VARIANTS, Loader } from 'cdk-radial';

import AuthComponent from '../common/AuthComponent';
import Page from '../page';
import LocationsDropdownMenu from './LocationsDropdownMenu';
import { mergeLocations } from '../../utils/location';
import { DataTransactionTable } from './DataTransactionTable';
import { getDataTransactions } from '../../redux/connectedComponent/dataTransaction/dataTransaction.slice';
import BrowseMarketplaceButton from '../subscriptionManagement/landingPage/BrowseMarketplaceButton';
import { sendAmplitudeData } from '../../utils/amplitude';
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from '../../constants/amplitudeConstants';
import './DataTransactionReport.scss';
import { PAGE_TITLE } from '../../constants/constants';
import { trackPageView, PAGE_SECTIONS, PAGE_SUBSECTIONS } from '../../utils/gtm';

const LOADER_STYLE = {
  marginTop: '200px',
  marginBottom: '400px',
  display: 'flex'
};

const collator = new Intl.Collator(undefined, { caseFirst: 'upper' });

function DataTransactionReport() {
  const { allLocations, authenticationToken, connectCdkToken } = useSelector(state => state.loginDetailsSlice);
  const { dataTransactions, isDataTransactionsLoading, hasDataTransactionsError } = useSelector(state => state.dataTransactionSlice);

  const dispatch = useDispatch();

  const mergedLocations = useMemo(() => mergeLocations(allLocations), [allLocations]);
  const selectOptions = useMemo(
    () => mergedLocations.map(location => ({
      label: location.partyName,
      value: location.entityId
    }))
      .sort((a, b) => collator.compare(a.label, b.label)),
    [mergedLocations]
  );

  const [selectedLocations, setSelectedLocations] = useState([]);
  const [dealerOrgIds, setDealerOrgIds] = useState([]);

  useEffect(() => {
    trackPageView({
      page_section: PAGE_SECTIONS.APP_MANAGEMENT,
      page_subsection: PAGE_SUBSECTIONS.DATA_TRANSACTION_REPORT,
    });
  }, []);

  useEffect(() => {
    setDealerOrgIds(selectedLocations.map(loc => loc.value));
  }, [selectedLocations]);

  const handleGenerateReport = () => {
    sendAmplitudeData(AMPLITUDE_EVENTS.GENERATE_TRANSACTION_REPORT, {
      [AMPLITUDE_PROPERTIES.LOCATION_COUNT]: selectedLocations?.length,
      [AMPLITUDE_PROPERTIES.LOCATIONS]: selectedLocations?.map(
        (loc) => ({
          [AMPLITUDE_PROPERTIES.ORG_ID]: loc?.value,
          [AMPLITUDE_PROPERTIES.ORG_NAME]: loc?.label,
        })
      ),
    });
    dispatch(getDataTransactions({dealerOrgIds, connectCdkToken, token: authenticationToken}));
  }

  return (
    <Page pageHeader={PAGE_TITLE.APP_MANAGEMENT}>
      <div className="transaction-report__container">
        <div className="transaction-report__heading">
          <div className="transaction-report__title">Data Transaction Report</div>
          <BrowseMarketplaceButton />
        </div>
        <div className="transaction-report__options">
          <LocationsDropdownMenu
            options={selectOptions}
            onChange={setSelectedLocations}
          />
          <Button
            text="Generate Report"
            variant={BUTTON_VARIANTS.PRIMARY}
            isDisabled={!selectedLocations.length || isDataTransactionsLoading}
            onClick={handleGenerateReport}
          />
        </div>
        {!isDataTransactionsLoading && dataTransactions &&
          <div className="transaction-report__table-section">
            <DataTransactionTable dataTransactionsLoading={isDataTransactionsLoading} dataTransactions={dataTransactions} selectedLocations={selectedLocations}/>
          </div>
        }
        {isDataTransactionsLoading &&
          <Loader data-testid="transaction-report-loading" style={LOADER_STYLE} label="Loading..." />
        }
        {!isDataTransactionsLoading && hasDataTransactionsError &&
          <div data-testid="transaction-report-error">
            <h1>Something went wrong.</h1>
            <h2>
              Please contact Fortellis Support at support@fortellis.io
            </h2>
          </div>
        }
      </div>
    </Page>
  );
}

export default AuthComponent(DataTransactionReport);
